import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
  IconButton,
  Stack,
  Button,
  CircularProgress,
  Box,
} from "@mui/material";
import { useState } from "react";
import { StyledInfoDocument } from "./styles";
import { formatDate } from "src/utils/formatters";
import { downloadDocuments } from "src/services/documents";
interface EmployeeProps {
  id: number;
  name: string;
  active: boolean;
}
interface DocumentProps {
  id?: number;
  name: string;
  employees?: EmployeeProps[];
  status: string;
  type?: string;
  expiration_date: string;
  public_hash: string;
}

interface DocumentListAccordionProps {
  documents: DocumentProps[];
  onDelete?: (documentId: number) => void;
  onEdit?: (document: DocumentProps) => void;
}

export default function DocumentListAccordion({
  documents,
  onDelete,
  onEdit,
}: DocumentListAccordionProps) {
  const [loadingId, setLoadingId] = useState<string | null>(null);

  const getLoggedUserRole = () => {
    const stored = localStorage.getItem("meData");
    if (!stored) return null;

    try {
      return JSON.parse(stored)?.role as string | null;
    } catch {
      return null;
    }
  };

  const isAdmin = getLoggedUserRole() === "admin";

  const handleDownloadDocument = async (publicHash?: string) => {
    if (!publicHash) {
      alert("Documento não encontrado.");
      return;
    }

    try {
      setLoadingId(publicHash);

      const response = await downloadDocuments(publicHash);
      if (response) {
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `doc-${publicHash}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Erro ao baixar documento:", error);
      alert("Erro ao baixar o documento. Tente novamente.");
    } finally {
      setLoadingId(null);
    }
  };

  const handleDeleteWithCheck = (documentId: string) => {
    if (!documentId) return;

    if (!isAdmin) {
      alert(
        "❌ Ação não permitida. Apenas administradores podem excluir documentos.",
      );
      return;
    }

    if (!window.confirm("Tem certeza que deseja excluir este documento?")) {
      return;
    }

    if (onDelete) onDelete(Number(documentId));
  };

  const getPriority = (status: string) => {
    if (status === "Vencido") return 1;
    if (status === "Ativo") return 2;
    if (status === "Renovado") return 3;
    return 4;
  };

  const sortedDocuments = [...documents].sort((a, b) => {
    const priorityA = getPriority(a.status);
    const priorityB = getPriority(b.status);
    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }
    const dateA = new Date(a.expiration_date).getTime();
    const dateB = new Date(b.expiration_date).getTime();
    return dateA - dateB;
  });

  return (
    <div>
      {sortedDocuments.map((doc, index) => {
        return (
          <Accordion
            key={doc.id || index}
            sx={{
              "&:before": { display: "none" },
              marginBottom: "8px",
              borderRadius: 2,
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: "#ffffff" }} />}
              id={`panel-${doc.public_hash || index}-header`}
              component="div"
              sx={(theme) => ({
                backgroundColor:
                  doc.status === "Vencido"
                    ? "#d32f2f"
                    : doc.status === "Ativo"
                      ? "#0B3095"
                      : "#2E7D32",
                borderRadius: 2,
                color: "#ffffff",
                minHeight: 40,
                "&.Mui-expanded": { minHeight: 40 },
                [theme.breakpoints.down("sm")]: {
                  minHeight: "auto",
                  paddingY: 1,
                },
              })}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ width: "100%" }}
              >
                <StyledInfoDocument as="span">
                  {doc.name} —{" "}
                  <strong style={{ color: "#ffffff" }}>
                    Válido até {formatDate(doc.expiration_date)}
                  </strong>{" "}
                  — {doc.status === "Ativo" ? "A vencer" : doc.status}
                </StyledInfoDocument>

                <Stack>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteWithCheck(String(doc.id));
                    }}
                    sx={{
                      color: "#fff",
                      "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                    }}
                    disabled={loadingId !== null}
                  >
                    <DeleteForeverIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>
            </AccordionSummary>

            <AccordionDetails
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                textTransform: "uppercase",
                paddingLeft: 2,
                paddingBottom: 2,
              }}
            >
              <Typography sx={{ fontSize: 14 }}>
                <StyledInfoDocument as="span">
                  Nome do Documento:
                </StyledInfoDocument>{" "}
                {doc.name}
              </Typography>
              {doc.employees?.[0] && (
                <Typography sx={{ fontSize: 14, display: "flex", alignItems: "center", gap: 1 }}>
                  <Box component="span">
                    <StyledInfoDocument as="span">
                      Funcionário:
                    </StyledInfoDocument>{" "}
                    {doc.employees[0].name}
                  </Box>
                  <Typography
                    component="span"
                    sx={{
                      fontSize: 12,
                      fontWeight: "bold",
                      color: doc.employees[0].active ? "#2e7d32" : "#d32f2f",
                      backgroundColor: doc.employees[0].active ? "#e8f5e9" : "#ffebee",
                      padding: "2px 8px",
                    }}
                  >
                    {doc.employees[0].active ? "Ativo" : "Inativo"}
                  </Typography>
                </Typography>
              )}
              <Typography sx={{ fontSize: 14 }}>
                <StyledInfoDocument as="span">Status:</StyledInfoDocument>{" "}
                {doc.status === "Ativo" ? "A vencer" : doc.status}
              </Typography>

              <Typography sx={{ fontSize: 14 }}>
                <StyledInfoDocument as="span">
                  Válido até: {formatDate(doc.expiration_date)}
                </StyledInfoDocument>
              </Typography>

              <Stack direction="row" spacing={2} mt={1}>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={
                    loadingId === doc.public_hash ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  onClick={() => handleDownloadDocument(doc.public_hash)}
                  disabled={loadingId === doc.public_hash}
                >
                  {loadingId === doc.public_hash
                    ? "Baixando..."
                    : "Baixar documento"}
                </Button>

                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => onEdit && onEdit(doc)}
                >
                  Editar Dados
                </Button>
              </Stack>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </div>
  );
}
